/**
 * Machine-readable codes for every way authentication or authorization can fail.
 *
 * The API emits these as the top-level `code` on the error envelope; the SPA branches on them.
 * Before this existed the SPA had to tell a permission failure from a CSRF failure by pattern
 * matching the human-readable `message` — which broke the moment either side reworded its prose.
 *
 * Dependency-free on purpose so the browser bundle can import it (see `auth/roles.ts`).
 */
export const AuthErrorCode = {
    /** No credentials presented at all. */
    AUTHENTICATION_REQUIRED: "AUTHENTICATION_REQUIRED",
    /** Credentials presented but not verifiable. */
    INVALID_TOKEN: "INVALID_TOKEN",
    /** Access token expired and the refresh token could not be rotated — re-login needed. */
    SESSION_EXPIRED: "SESSION_EXPIRED",
    /** A rotated-out refresh token was replayed; the whole token family was revoked. */
    TOKEN_REUSE_DETECTED: "TOKEN_REUSE_DETECTED",
    /** The presented refresh token has no stored row. */
    TOKEN_NOT_FOUND: "TOKEN_NOT_FOUND",
    /** Refresh failed for any other reason. */
    REFRESH_FAILED: "REFRESH_FAILED",
    /** Double-submit CSRF check failed on a state-changing request. */
    CSRF_INVALID: "CSRF_INVALID",
    /** Authenticated, but the account's role does not admit this route. */
    INSUFFICIENT_PERMISSIONS: "INSUFFICIENT_PERMISSIONS",
    /** The role admits the route, but the record it names belongs to another company. */
    RECORD_ACCESS_DENIED: "RECORD_ACCESS_DENIED",
    /** Password-reset link is malformed, not a reset link, or spent (the password changed since). */
    RESET_LINK_INVALID: "RESET_LINK_INVALID",
    /** Password-reset link is well-formed but past its expiry. */
    RESET_LINK_EXPIRED: "RESET_LINK_EXPIRED",
    /** Login refused: no account has this address, or the password is wrong. One code for both, so
     * the answer doesn't tell whether the address has an account. */
    INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
};
/**
 * Codes that mean "your session is broken, log in again" rather than "you may not do this".
 * The SPA must not offer these to the user as a permissions problem.
 */
export const SESSION_ERROR_CODES = [
    AuthErrorCode.AUTHENTICATION_REQUIRED,
    AuthErrorCode.INVALID_TOKEN,
    AuthErrorCode.SESSION_EXPIRED,
    AuthErrorCode.TOKEN_REUSE_DETECTED,
    AuthErrorCode.TOKEN_NOT_FOUND,
    AuthErrorCode.REFRESH_FAILED,
    AuthErrorCode.CSRF_INVALID,
];
